import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { WS_URL } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';

const WsContext = createContext(null);

export function WsProvider({ children }) {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    let closed = false;
    let reconnectTimer;
    const connect = () => {
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!closed) reconnectTimer = setTimeout(connect, 2500);
      };
      ws.onerror = (ev) => { console.debug('[WS] error', ev); };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          setLastEvent({ ...data, receivedAt: Date.now() });
          listenersRef.current.forEach((fn) => {
            try { fn(data); } catch (e) { console.debug('[WS] listener error', e); }
          });
        } catch (e) {
          // Non-JSON heartbeat frames (e.g. "pong") — aman diabaikan tapi tetap log di debug.
          console.debug('[WS] non-JSON frame (heartbeat/pong):', msg?.data);
        }
      };
    };
    connect();
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === 1) wsRef.current.send('ping');
    }, 25000);
    return () => {
      closed = true;
      clearInterval(ping);
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [token, user]);

  const value = useMemo(
    () => ({ connected, lastEvent, subscribe }),
    [connected, lastEvent, subscribe],
  );

  return (
    <WsContext.Provider value={value}>
      {children}
    </WsContext.Provider>
  );
}

export const useWs = () => useContext(WsContext);
